import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueries, useQuery } from '@tanstack/react-query';
import { showcaseApi } from '../../common-showcase/api/showcaseApi';
import { RECIPES } from '../../common-showcase/data/recipes';
import { PITFALLS } from '../../common-showcase/data/pitfalls';
import { DECISION_GUIDES } from '../../common-showcase/data/decision-guides';
import configPropertiesJson from '../../common-showcase/data/config-properties.json';
import type { ConfigProperties } from '../../common-showcase/types/showcase';
import { catalogApi } from '../../_index/api/catalogApi';
import { menuApi } from '../../menu-management/api/menuApi';
import { devStandardsApi } from '../../dev-standards-viewer/api/devStandardsApi';
import { terminologyApi } from '../../terminology-management/api/terminologyApi';
import {
  resolveConsumerGuideAsset,
  type ConsumerGuideAssetLike,
} from '../../getting-started-guide/lib/resolveConsumerGuideAsset';
import {
  buildCapabilityHits,
  buildDecisionHits,
  buildRecipeHits,
  buildConfigHits,
  buildPitfallHits,
  buildBizHits,
  buildMenuHits,
  buildStandardsHits,
  buildDocHits,
  buildSpecHits,
  buildTermHits,
  filterHits,
  groupHits,
  shouldSearchTerms,
  type SearchGroup,
  type SearchSource,
} from '../data/searchIndex';
import { useDebouncedValue } from './useDebouncedValue';

interface ConsumerGuideAsset extends ConsumerGuideAssetLike {
  content: string;
  extractedAt: string;
  sourcePath: string;
}

/**
 * queue #689 §UAT-2026-07-14-R689 §R689-1-② — 소비자 가이드 KO/EN 번들 JSON 을 팔레트 오픈
 * 시점에 **동적 import** 한다(정적 import 는 app shell/entry 청크에 편입되므로 금지 —
 * §R689-2 entry 청크 증가 0 목표). `ConsumerGuidePage` 는 여전히 정적 import 를 쓰지만, 그
 * 페이지 자체가 별도 라우트라 이 훅(전역 팔레트)과는 무관한 번들 경계다.
 */
async function loadConsumerGuideAssets(): Promise<{ ko: ConsumerGuideAsset; en: ConsumerGuideAsset }> {
  const [koModule, enModule] = await Promise.all([
    import('../../getting-started-guide/data/consumer-guide.md.json'),
    import('../../getting-started-guide/data/consumer-guide.en.md.json'),
  ]);
  return {
    ko: koModule.default as ConsumerGuideAsset,
    en: enModule.default as ConsumerGuideAsset,
  };
}

// queue #665 §R665-5: 설정 속성은 로컬 정적 데이터(config-properties.json) — 별도 fetch 없이
// 즉시 인메모리 인덱싱한다(BE 0 · recipe 와 동형).
const CONFIG_PROPERTIES_DATA = configPropertiesJson as unknown as ConfigProperties;

/** §R659-1-3: 초기 LCP 무영향 — 팔레트 최초 오픈 시 lazy 페치 + staleTime 길게(5분) */
const LAZY_STALE_TIME_MS = 5 * 60 * 1000;
/** §R659-2-⑤: 서버 keyword 검색 debounce */
const TERM_DEBOUNCE_MS = 300;

export interface UseSearchIndexResult {
  /** 5 소스 고정 순서 그룹핑 + 그룹당 상위 5 절단 결과 */
  groups: SearchGroup[];
  /** 소스별 "최초 로딩 중" 여부(격리) — 그룹 스켈레톤에 사용 */
  sourceLoading: Record<SearchSource, boolean>;
  /** 소스별 실패 여부(격리, allSettled 동형) — 그룹 인라인 오류에 사용 */
  sourceErrors: Record<SearchSource, boolean>;
  /** 질의 2자 이상 + 전 소스 0건 + 로딩 중 아님 */
  isEmpty: boolean;
  /** 질의 공백 여부 */
  hasQuery: boolean;
}

/**
 * #659 §UAT-2026-07-08-R659 §R659-3 — 팔레트 오픈 시 5종 검색 소스를 lazy 로 인덱싱한다.
 * ①②③④ 는 오픈 시 1회(`enabled: isOpen`) 인메모리 인덱싱 + 클라이언트 필터, ⑤ 는
 * `keyword` debounce(300ms)마다 서버 위임 검색. `useQueries` 로 소스별 상태를 격리해
 * 한 소스의 실패가 나머지에 영향을 주지 않는다(§R659-1-4 allSettled 동형).
 */
export function useSearchIndex(isOpen: boolean, query: string): UseSearchIndexResult {
  const { t, i18n } = useTranslation();
  const debouncedQuery = useDebouncedValue(query, TERM_DEBOUNCE_MS);
  const trimmedQuery = debouncedQuery.trim();

  const [capabilityQuery, bizQuery, menuQuery, standardsQuery, docAssetQuery] = useQueries({
    queries: [
      {
        queryKey: ['_shared', 'search-index', 'capability'] as const,
        queryFn: () => showcaseApi.getModules(),
        enabled: isOpen,
        staleTime: LAZY_STALE_TIME_MS,
      },
      {
        queryKey: ['_shared', 'search-index', 'biz'] as const,
        queryFn: () => catalogApi.getCatalog(),
        enabled: isOpen,
        staleTime: LAZY_STALE_TIME_MS,
      },
      {
        queryKey: ['_shared', 'search-index', 'menu'] as const,
        queryFn: () => menuApi.getMyMenus(),
        enabled: isOpen,
        staleTime: LAZY_STALE_TIME_MS,
      },
      {
        queryKey: ['_shared', 'search-index', 'standards'] as const,
        queryFn: () => devStandardsApi.getActive(),
        enabled: isOpen,
        staleTime: LAZY_STALE_TIME_MS,
      },
      {
        // queue #689 §R689-1-② — 소비자 가이드 KO/EN 번들 동적 import(신규 통신 요청 0 ·
        // 이미 빌드에 존재하는 JSON 청크를 팔레트 오픈 시에만 가져온다).
        queryKey: ['_shared', 'search-index', 'doc'] as const,
        queryFn: loadConsumerGuideAssets,
        enabled: isOpen,
        staleTime: LAZY_STALE_TIME_MS,
      },
    ],
  });

  const termsEnabled = isOpen && shouldSearchTerms(trimmedQuery);
  const termsQuery = useQuery({
    // ⑤ 는 keyword 재사용 검색 — terminology-management 의 기존 queryKey 패턴과 동일
    queryKey: ['terminology', 'standard-terms', 'list', { keyword: trimmedQuery, size: 8 }],
    queryFn: () => terminologyApi.listStandardTerms({ keyword: trimmedQuery, size: 8 }),
    enabled: termsEnabled,
    staleTime: 15_000,
  });

  /**
   * queue #693 §UAT-2026-07-14-R693 §R693-5 — 모듈 스펙 본문 전문 검색은 BE 위임(`term` 소스와
   * 동일한 debounce(300ms)/최소 2자 가드 재사용 — 신규 상수 0). 첫 질의는 서버 인덱스 콜드
   * 빌드로 수 초 걸릴 수 있어 `sourceLoading.spec` 격리가 특히 중요(§R693-5).
   */
  const specEnabled = isOpen && shouldSearchTerms(trimmedQuery);
  const specQuery = useQuery({
    queryKey: ['_shared', 'search-index', 'spec', { q: trimmedQuery }],
    queryFn: () => showcaseApi.searchSpecs(trimmedQuery),
    enabled: specEnabled,
    staleTime: 15_000,
  });

  const capabilityHits = useMemo(
    () => filterHits(buildCapabilityHits(capabilityQuery.data?.nodes ?? [], t), trimmedQuery),
    [capabilityQuery.data, t, trimmedQuery]
  );
  // queue #667 §R667-5: 선택 가이드도 로컬 정적 데이터(DECISION_GUIDES) — recipe/pitfall 과
  // 동형(sourceLoading/sourceErrors 는 항상 false).
  const decisionHits = useMemo(
    () => filterHits(buildDecisionHits(DECISION_GUIDES, t), trimmedQuery),
    [t, trimmedQuery]
  );
  // queue #664 §R664-5: 레시피는 로컬 정적 데이터(RECIPES) — 별도 fetch/query 없이 즉시
  // 인메모리 인덱싱한다(BE 0 · sourceLoading/sourceErrors 는 항상 false).
  const recipeHits = useMemo(
    () => filterHits(buildRecipeHits(RECIPES, t), trimmedQuery),
    [t, trimmedQuery]
  );
  // queue #665 §R665-5: 로컬 정적 데이터 — sourceLoading/sourceErrors 는 항상 false(recipe 동형).
  const configHits = useMemo(
    () => filterHits(buildConfigHits(CONFIG_PROPERTIES_DATA), trimmedQuery),
    [trimmedQuery]
  );
  // queue #666 §R666-5: 알려진 함정도 로컬 정적 데이터(PITFALLS) — recipe/config 와 동형
  // (sourceLoading/sourceErrors 는 항상 false).
  const pitfallHits = useMemo(
    () => filterHits(buildPitfallHits(PITFALLS, t), trimmedQuery),
    [t, trimmedQuery]
  );
  const bizHits = useMemo(
    () => filterHits(buildBizHits(bizQuery.data?.biz ?? []), trimmedQuery),
    [bizQuery.data, trimmedQuery]
  );
  const menuHits = useMemo(
    () => filterHits(buildMenuHits(menuQuery.data ?? []), trimmedQuery),
    [menuQuery.data, trimmedQuery]
  );
  /**
   * queue #689 §UAT-2026-07-14-R689 §R689-2 ★기존 성능 결함 정정 — 개발표준 마크다운 파싱
   * (`buildStandardsHits`: github-slugger 로 전 헤딩 재슬러그 + 본문 발췌)을 예전엔 `query`
   * 도 dep 에 넣은 단일 memo 로 묶어 **키입력마다 전체 재파싱**했다. build(파싱, 데이터
   * dep 전용)과 filter(질의, query dep 전용) memo 를 분리해 파싱은 데이터가 바뀔 때만 돈다.
   */
  const standardsHitsAll = useMemo(
    () => buildStandardsHits(standardsQuery.data?.markdownText ?? ''),
    [standardsQuery.data]
  );
  const standardsHits = useMemo(
    () => filterHits(standardsHitsAll, trimmedQuery),
    [standardsHitsAll, trimmedQuery]
  );

  // #689 §R689-1-⑥ — ConsumerGuidePage 와 동일 SSOT 판정 함수로 KO/EN 자산을 선택한다.
  const docAssetSelection = useMemo(() => {
    if (!docAssetQuery.data) return null;
    return resolveConsumerGuideAsset(i18n.language, docAssetQuery.data.ko, docAssetQuery.data.en);
  }, [docAssetQuery.data, i18n.language]);

  // #689 §R689-2 — doc 소스도 standards 와 동형 2단 memo(빌드: 자산/언어 dep · 필터: query dep).
  const docHitsAll = useMemo(() => {
    if (!docAssetSelection) return [];
    return buildDocHits({
      docTitle: t('gettingStartedGuide.consumerGuide.title'),
      markdownText: docAssetSelection.asset.content,
      hrefBase: '/getting-started/consumer-guide',
    });
  }, [docAssetSelection, t]);
  const docHits = useMemo(() => filterHits(docHitsAll, trimmedQuery), [docHitsAll, trimmedQuery]);

  const termHits = useMemo(
    () => (termsEnabled ? buildTermHits(termsQuery.data?.content ?? []) : []),
    [termsEnabled, termsQuery.data]
  );
  // queue #693 §R693-5: 서버가 이미 필터/정렬 → 클라 재필터 없이 매핑만(`term` 동형).
  const specHits = useMemo(
    () => (specEnabled ? buildSpecHits(specQuery.data?.hits ?? []) : []),
    [specEnabled, specQuery.data]
  );

  const groups = useMemo(
    () =>
      groupHits([
        ...capabilityHits,
        ...decisionHits,
        ...recipeHits,
        ...configHits,
        ...pitfallHits,
        ...bizHits,
        ...menuHits,
        ...standardsHits,
        ...docHits,
        ...specHits,
        ...termHits,
      ]),
    [
      capabilityHits,
      decisionHits,
      recipeHits,
      configHits,
      pitfallHits,
      bizHits,
      menuHits,
      standardsHits,
      docHits,
      specHits,
      termHits,
    ]
  );

  const sourceLoading: Record<SearchSource, boolean> = {
    capability: capabilityQuery.isLoading,
    decision: false,
    recipe: false,
    config: false,
    pitfall: false,
    biz: bizQuery.isLoading,
    menu: menuQuery.isLoading,
    standards: standardsQuery.isLoading,
    doc: docAssetQuery.isLoading,
    spec: specEnabled && specQuery.isLoading,
    term: termsEnabled && termsQuery.isLoading,
  };

  const sourceErrors: Record<SearchSource, boolean> = {
    capability: capabilityQuery.isError,
    decision: false,
    recipe: false,
    config: false,
    pitfall: false,
    biz: bizQuery.isError,
    menu: menuQuery.isError,
    standards: standardsQuery.isError,
    doc: docAssetQuery.isError,
    spec: specEnabled && specQuery.isError,
    term: termsEnabled && termsQuery.isError,
  };

  const hasQuery = trimmedQuery.length > 0;
  const isAnyLoading = Object.values(sourceLoading).some(Boolean);
  const isEmpty = shouldSearchTerms(trimmedQuery) && !isAnyLoading && groups.length === 0;

  return { groups, sourceLoading, sourceErrors, isEmpty, hasQuery };
}
